<template>
  <SamMenu
    :options="menuOptions"
    :close-on-content-click="!multiple"
    class="sam-chip-select"
  >
    <template #activator="{ on, isMenuOpen, toggle }">
      <a
        :class="[`sam-chip-select__menu-activator`, { 'is-disabled': disabled }]"
        v-on="disabled ? {} : on"
      >
        <SamChip
          sam-type="outlined"
          :label="label"
          :disabled="disabled"
          :active="isChipActive"
        >
          <template #append>
            <div class="d-flex middle-xs">
              <SamIcon
                v-if="shouldBeClearable"
                class="sam-chip-select__clear-icon"
                icon="interface-delete"
                size="xs"
                @click.stop="clear"
              />

              <SamIcon
                class="sam-chip-select__expand-icon"
                :icon="isMenuOpen ? 'arrow-chevron-up' : 'arrow-chevron-down'"
                size="sm"
                @click.stop="toggle"
              />
            </div>
          </template>
          <slot
            name="label"
            :value="selectedValue"
            :selection="selectedOption"
          />
        </SamChip>
      </a>
    </template>

    <template v-if="!items.length">
      <div
        v-if="!$slots['no-data']"
        class="sam-chip-select__no-data"
      >
        No data to show
      </div>
      <template v-else>
        <slot name="no-data" />
      </template>
    </template>
    <SamSelectList
      v-else
      v-model="selectedValue"
      :items="items"
      :multiple="multiple"
      :has-select-all="hasSelectAll"
      @item-click="onItemSelection"
    />
  </SamMenu>
</template>

<script>
import SamMenu from '../SamMenu/SamMenu.vue';
import SamChip from '../SamChip/SamChip.vue';
import SamIcon from '../SamIcon/SamIcon.vue';
import { objectHasOwnProperties } from '../../../utils/utils';
import SamSelectList from '../SamSelectList/SamSelectList.vue';

export default {
  name: 'SamChipSelect',
  components: {
    SamSelectList,
    SamIcon,
    SamMenu,
    SamChip,
  },
  model: {
    event: 'change',
  },
  props: {
    /**
     * The options for the dropdown
     */
    items: {
      type: Array,
      required: true,
      default: () => [],
      validator: (val) => val.every?.((item) => objectHasOwnProperties(item, 'label', 'value')),
    },
    /**
     * The selection value
     */
    value: {
      type: [Number, String, Array],
      required: false,
      default: undefined,
    },
    /**
     * The SamMenu options to be applied
     */
    menuOptions: {
      type: Object,
      required: false,
      default: () => ({}),
    },
    /**
     * If the selection is disabled
     * @values true, false
     */
    disabled: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * If the current selection can be cleared out
     * @values true, false
     */
    withClear: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * Clear value if the current selection can be cleared out
     */
    clearValue: {
      type: [String, Number, undefined, null],
      required: false,
      default: null,
    },
    /**
     * If it allows multiple selection
     */
    multiple: {
      type: Boolean,
      required: false,
      default: false,
    },
    /**
     * The Chip label
     */
    label: {
      type: [Number, String],
      required: true,
    },
    hasSelectAll: {
      type: Boolean,
      default: false,
      required: false,
    },
  },
  data() {
    return {
      selectedValue: this.value ?? (this.multiple ? [] : this.clearValue),
    };
  },
  computed: {
    isChipActive() {
      if (this.multiple) {
        return this.selectedValue?.length > 0;
      }
      return this.selectedValue !== this.clearValue;
    },
    selectedOption() {
      if (this.multiple) {
        return this.items.filter((item) => this.selectedValue.includes(item.value));
      }
      return this.items.find((item) => item.value === this.value);
    },
    shouldBeClearable() {
      return this.withClear && this.selectedValue !== this.clearValue;
    },
  },
  watch: {
    selectedValue: {
      handler(value) {
        this.$emit('change', value);
      },
      deep: true,
    },
  },
  methods: {
    onItemSelection(item) {
      this.$emit('item-selection', { item });
    },
    clear() {
      if (this.multiple) {
        this.selectedValue = [];
      } else {
        this.selectedValue = this.clearValue;
      }
    },
  },
};
</script>

<style scoped lang="scss">
.sam-chip-select__menu-activator {
  display: block;

  .sam-chip-select__clear-icon {
    margin-left: $size--2;
  }

  .sam-chip-select__no-data {
    @extend .text--caption, .p-4;
    color: $neutral--60;
    font-style: italic;
    text-align: center;
  }

  &.is-disabled {
    cursor: not-allowed;

    .sam-select__expand-icon {
      color: $neutral--50;
    }
  }
}
</style>
